import { t } from '@repo/i18n';
import { useLocale } from '@repo/i18n/react';
import type { Assessment, KeyMember } from '@repo/shared/schema';
import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { OLink, RiskBadge } from '@/components/raven/shared';
import { trpc } from '@/providers/trpc';
import { SignalsSection } from './signals-section';

export const KeyMembersAccordion = ({
  keyMembers,
  reportId,
  assessment,
  compact = false,
}: {
  keyMembers: KeyMember[];
  reportId: string;
  assessment?: Assessment;
  compact?: boolean;
}) => {
  useLocale();

  const [selected, setSelected] = useState<string | null>(null);
  const { hash } = useLocation();
  useEffect(() => {
    const id = hash.replace('#member-', '');
    if (hash.startsWith('#member-') && keyMembers.some((member) => member._id === id)) {
      setSelected(id);
      document
        .getElementById(`member-${id}`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [hash, keyMembers]);

  const list = useRef<HTMLDivElement>(null);
  const generation = trpc.report.getReportGeneration.useQuery({ reportId });
  const member = keyMembers.find((member) => member._id === selected);
  return (
    <section className="raven-section" id="key-people">
      <span className="raven-label">{t('auth.keyPeople2')}</span>
      <div className="mb-4 flex items-center justify-between">
        <h2>{t('auth.keyStakeholdersMembers')}</h2>
        <div className="flex gap-2">
          <button
            aria-label={t('auth.previousMembers')}
            onClick={() => list.current?.scrollBy({ left: -280, behavior: 'smooth' })}
            type="button"
          >
            <ChevronLeftIcon className="size-5 rtl:rotate-180" />
          </button>
          <button
            aria-label={t('auth.nextMembers')}
            onClick={() => list.current?.scrollBy({ left: 280, behavior: 'smooth' })}
            type="button"
          >
            <ChevronRightIcon className="size-5 rtl:rotate-180" />
          </button>
        </div>
      </div>
      <div className="flex snap-x gap-4 overflow-x-auto pb-3" ref={list}>
        {keyMembers.map((member) => (
          <button
            aria-expanded={selected === member._id}
            className="raven-panel min-w-[240px] flex-1 snap-start border border-transparent text-start hover:border-[#c09ab4]"
            id={`member-${member._id}`}
            key={member._id}
            onClick={() => setSelected(selected === member._id ? null : member._id)}
            type="button"
          >
            <div className="flex gap-3">
              <span className="flex size-12 shrink-0 items-center justify-center rounded-xl bg-[#e8e7ee] text-sm">
                {member.firstName[0]}
                {member.lastName[0]}
              </span>
              <div>
                <h3 className="text-xs">
                  {member.firstName} {member.lastName}
                </h3>
                <p className="raven-muted">{member.memberRole}</p>
                <RiskBadge
                  level={
                    assessment?.subjects.find((subject) => subject.subjectId === member._id)
                      ?.riskLevel
                  }
                />
              </div>
            </div>
            <p className="mt-5 font-medium text-xs">{t('auth.personalInformation')}</p>
            <dl className="mt-3 space-y-2 text-xs">
              {[
                [t('reports.nationality'), member.nationality],
                [t('reports.netWorth'), member.estimatedNetWorth],
                [t('reports.countryOfResidence'), member.countryOfResidence],
              ].map(([label, value]) => (
                <div className="flex justify-between gap-3" key={label}>
                  <dt className="raven-muted">{label}</dt>
                  <dd>{value ?? t('extra.notProvided')}</dd>
                </div>
              ))}
            </dl>
            <ChevronDownIcon
              className={`mx-auto mt-4 size-5 ${selected === member._id ? 'rotate-180' : ''}`}
            />
          </button>
        ))}
      </div>
      {!keyMembers.length && <p className="raven-panel">{t('auth.noKeyMembersRecorded')}</p>}
      {member && (
        <div className="mt-3 rounded-2xl border border-[#ce98b1] bg-[#faf6f8] p-5">
          <div className="mb-4 flex justify-between">
            <span className="raven-muted">{t('landing.profileDecision')}</span>
            {compact && (
              <OLink to={`/reports/${reportId}#member-${member._id}`}>
                {t('auth.viewStakeholderFullKycResult')}{' '}
              </OLink>
            )}
          </div>
          <div className="raven-panel mb-4">
            <h3>{t('landing.riskSummary')}</h3>
            <p className="mt-2 text-sm">
              {generation.data?.progress.find((progress) => progress.subjectId === member._id)
                ?.summary || t('extra.noEvidenceSummaryAvailable')}
            </p>
          </div>
          {!compact && (
            <div className="space-y-5">
              <h3>{t('reports.findings')}</h3>
              <SignalsSection
                memberId={member._id}
                reportId={reportId}
                signals={member.signals ?? []}
              />
            </div>
          )}
        </div>
      )}
    </section>
  );
};
